/**
 * Outstanding refunds, rebuilt on the template's own pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/financial/dashboard/transaction-history-table/index.tsx
 *   the table card and its queued/failed status filter (`PagedTable`'s `StatusField` dropdown);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the two state cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value rows in the "what to do" card.
 * The header is `components/detail-parts.tsx`'s `PageHero` (profile-header.tsx's cover band).
 *
 * The screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 *
 * Rules this screen keeps: queued and failed are never summed, and no amount is totalled — the
 * service publishes no "money in flight" figure, so none is drawn. The two state counts are the
 * lengths of the API's own list split by the status it sent.
 */
import { Link, useLoaderData, useOutletContext, useSearchParams } from "react-router-dom";
import {
  PiArrowCounterClockwiseDuotone as RefundIcon,
  PiArrowRightBold as ArrowRight,
  PiCheckCircleDuotone as CheckCircle2,
  PiCreditCardDuotone as CardIcon,
  PiHourglassMediumDuotone as QueuedIcon,
  PiQuestionDuotone as HelpCircle,
  PiWarningOctagonDuotone as FailedIcon,
  PiXBold as ClearIcon,
} from "react-icons/pi";
import { Badge, Button, Text } from "rizzui";
import { CaveatBanner, EmptyState, ErrorSurface, MetricCard, WidgetCard } from "@steelyard/theme";
import { can } from "../../lib/permissions";
import { bucketRefunds, FAILED, QUEUED, RETRY_SCHEDULE_NOTICE, SUCCEEDED, getOutstandingRefunds, type OutstandingRefund } from "../../lib/services/finance";
import type { AccountSummary, PermissionState } from "../../types";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { CardTitle, InfoBlock, PageHero } from "../../components/detail-parts";
import { hrefWith } from "../../lib/paging";
import { refundColumns } from "./columns";

/** Ported from apps/admin/app/(app)/finance/refunds/page.tsx. Queued and failed are never summed. */
export async function refundsLoader() {
  // Each refund carries its order number, customer and provider reference from Billing itself.
  // Completed refunds are a second read, always made, so the Refunded option carries its count
  // before it is chosen and "refunds against this payment" includes the finished ones.
  const [outstanding, succeeded] = await Promise.all([getOutstandingRefunds(), getOutstandingRefunds(SUCCEEDED)]);
  return { refunds: outstanding.refunds, succeeded: succeeded.refunds };
}

export default function RefundsPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const data = useLoaderData() as { refunds: OutstandingRefund[]; succeeded: OutstandingRefund[] };
  const [searchParams] = useSearchParams();

  if (!can(permissionState, "billing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view the refund queue" />;
  }
  const mayRetry = can(permissionState, "billing.administer");

  const statusParam = searchParams.get("status");
  const paymentId = searchParams.get("paymentId") ?? undefined;
  const status = statusParam === QUEUED || statusParam === FAILED || statusParam === SUCCEEDED ? statusParam : undefined;

  const forPayment = (rows: OutstandingRefund[]) => (paymentId ? rows.filter((r) => r.paymentId === paymentId) : rows);
  const all = forPayment(data.refunds);
  const refunded = forPayment(data.succeeded);
  const buckets = bucketRefunds(all);
  // The payment chip reads as its order number / provider reference, never the payment id.
  const first = all[0] ?? refunded[0];
  const paymentLabel = paymentId ? (first?.orderNumber ?? first?.providerReference ?? "this payment") : null;
  const shown =
    status === QUEUED ? buckets.queued
    : status === FAILED ? buckets.failed
    : status === SUCCEEDED ? refunded
    : [...all, ...refunded];

  // Every control resets to a fresh table (below) — a filter change means a different result set.
  // `hrefWith` keeps `paymentId` and `pageSize`.
  const query = (over: { status?: string; paymentId?: string }) => hrefWith("/finance/refunds", searchParams, over);

  // The counts are the lengths of the API's own lists — the same numbers the state cards show, never
  // added together. No "all" option: with no state chosen the table lists every refund, and Reset
  // returns to that.
  const stateOptions: StatusOption[] = [
    { label: "Queued", href: query({ status: QUEUED }), active: status === QUEUED, count: buckets.queued.length, status: QUEUED },
    { label: "Failed", href: query({ status: FAILED }), active: status === FAILED, count: buckets.failed.length, status: FAILED },
    { label: "Succeeded", href: query({ status: SUCCEEDED }), active: status === SUCCEEDED, count: refunded.length, status: SUCCEEDED },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Finance" }, { name: "Refunds" }]}
        icon={<RefundIcon aria-hidden className="size-9" />}
        iconClassName="bg-blue"
        title="Refunds"
        description="Every refund. Queued and failed ones are money still owed to the customer; succeeded ones have reached them."
        meta={
          <>
            <Badge variant="flat" color="secondary" rounded="pill" className="tabular-nums">
              {shown.length} shown
            </Badge>
            {paymentId ? (
              <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
                <CardIcon aria-hidden className="size-4" />
                Payment <span className="font-mono">{paymentLabel}</span>
                <Link to={query({ paymentId: undefined })} aria-label="Show refunds for every payment" className="text-gray-500 hover:text-gray-900">
                  <ClearIcon aria-hidden className="size-3" />
                </Link>
              </Badge>
            ) : null}
          </>
        }
        actions={
          <>
            <Link to="/finance/payments">
              <Button as="span" variant="outline">
                <CardIcon aria-hidden className="me-1.5 size-4" />
                Payments
              </Button>
            </Link>
            <Link to="/finance/transactions">
              <Button as="span" variant="outline">
                Gateway transactions
              </Button>
            </Link>
          </>
        }
      />

      {/* ── The three states, and what the two outstanding ones ask of a person ──────────── */}
      <div className="grid gap-4 @container md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title="Queued — not reached the provider"
          metric={buckets.queued.length}
          icon={<QueuedIcon aria-hidden className="size-6" />}
          iconClassName="bg-blue text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Nothing refused, nothing known yet.</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        >
          <StateLink to={query({ status: QUEUED })} active={status === QUEUED} label="Show queued" />
        </MetricCard>
        <MetricCard
          title="Failed — the provider refused"
          metric={buckets.failed.length}
          icon={<FailedIcon aria-hidden className="size-6" />}
          iconClassName="bg-red text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Error retained. Nothing retries on its own.</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        >
          <StateLink to={query({ status: FAILED })} active={status === FAILED} label="Show failed" />
        </MetricCard>
        <MetricCard
          title="Succeeded — refunded by the provider"
          metric={refunded.length}
          icon={<CheckCircle2 aria-hidden className="size-6" />}
          iconClassName="bg-green text-white shadow-sm"
          info={<Text className="mt-0.5 text-xs text-gray-500">Reached the customer. Nothing left to do.</Text>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        >
          <StateLink to={query({ status: SUCCEEDED })} active={status === SUCCEEDED} label="Show succeeded" />
        </MetricCard>

        <WidgetCard
          rounded="lg"
          title={
            <CardTitle icon={<CheckCircle2 aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
              What each one needs
            </CardTitle>
          }
          headerClassName="items-center"
        >
          <div className="mt-4 flex flex-col gap-3">
            <InfoBlock icon={<QueuedIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Queued">
              <Text className="text-sm text-gray-700">Wait, or escalate the provider connection. Not a record to fix.</Text>
            </InfoBlock>
            <InfoBlock icon={<FailedIcon aria-hidden className="size-4" />} tile="bg-red-lighter text-red-dark" label="Failed">
              <Text className="text-sm text-gray-700">Read the provider&rsquo;s reason, fix the cause, then retry from the refund.</Text>
            </InfoBlock>
          </div>
        </WidgetCard>
      </div>

      {buckets.unrecognised.length > 0 ? (
        <div role="alert">
          <MetricCard
            title="Status not recognised"
            metric={buckets.unrecognised.length}
            icon={<HelpCircle aria-hidden className="size-6" />}
            iconClassName="bg-orange-lighter text-orange-dark"
            info={<Text className="mt-0.5 text-xs text-gray-500">A status this screen does not know. Shown as it arrived in the table below — report it.</Text>}
            rounded="lg"
          />
        </div>
      ) : null}

      <CaveatBanner
        caveat={
          status === SUCCEEDED ? "Every refund below has been refunded by the provider"
          : status ? "Every refund below is money owed, and a decision for a person"
          : "Queued and failed refunds below are money owed, and a decision for a person"
        }
        action={status === SUCCEEDED ? undefined : <span>{RETRY_SCHEDULE_NOTICE}</span>}
      >
        {/* Both lists arrive whole (one call each), so PagedTable pages them in memory. Keyed per
            filter so the pager starts from page 1 on a different result set. */}
        <PagedTable<OutstandingRefund>
          key={`${status ?? "all"}:${paymentId ?? "none"}`}
          result={{ items: shown, page: 1, pageSize: shown.length, totalCount: shown.length }}
          columns={refundColumns(mayRetry)}
          getRowId={(row) => row.refundId}
          caption="Refunds: amount, state, reason, order, attempts and last attempt"
          status={{ label: "State", options: stateOptions, placeholder: "All refunds" }}
          clear={{ active: Boolean(status || paymentId), href: query({ status: undefined, paymentId: undefined }) }}
          rowClassName={(row) => (row.status === FAILED ? "bg-red-lighter/20" : undefined)}
          empty={<NothingOutstanding filtered={Boolean(status || paymentId)} />}
        />
      </CaveatBanner>
    </div>
  );
}

function StateLink({ to, active, label }: { to: string; active: boolean; label: string }) {
  return (
    <div className="mt-4 border-t border-dashed border-muted pt-3">
      {active ? (
        <Text className="text-sm font-medium text-gray-500">Showing only these</Text>
      ) : (
        <Link to={to} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-dark hover:underline">
          {label}
          <ArrowRight aria-hidden className="size-3.5" />
        </Link>
      )}
    </div>
  );
}

function NothingOutstanding({ filtered }: { filtered: boolean }) {
  if (filtered) {
    return (
      <EmptyState title="Nothing matched this filter" action={<Link to="/finance/refunds" className="text-sm font-medium text-blue-dark hover:underline">Show every refund</Link>}>
        The filter removed the evidence either way, so this says nothing about the other refunds. Clear it to see them all.
      </EmptyState>
    );
  }
  return (
    <EmptyState title="No refunds yet" icon={<CheckCircle2 aria-hidden className="size-16 text-green" />}>
      Billing has no refund on record — nothing queued, failed or refunded. This is an empty list, not one that failed to load.
    </EmptyState>
  );
}
